import { Button, Card, CardBody, Flex, Icon, Stack, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FiEdit3 } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import {
  PortalLifecycleStatus,
  useCreatePublicBidMutation,
  useGetPortalRegistrationStatusQuery,
  type PortalSourcingEventDetailDto,
} from "../../../../autogen/bff-api";
import { useApiError } from "../../../../common/errors/useApiError";
import { useAppSelector } from "../../../../common/redux/hooks";
import { urls } from "../../../../urls";

export const StartBidCta = ({ event }: { event: PortalSourcingEventDetailDto }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const displayer = useApiError();
  const authState = useAppSelector((state) => state.authState.state);
  const [createBid, { isLoading }] = useCreatePublicBidMutation();

  const isLoggedInWithOrg = authState?.type === "LoggedInWithOrgContext";
  const isOpen = event.lifecycle.status === PortalLifecycleStatus.Open;

  // Registering interest is the entry point; the offer CTA only appears once the supplier is following the procurement.
  const { data: status } = useGetPortalRegistrationStatusQuery(
    { eventId: event.id },
    { skip: !isLoggedInWithOrg || !isOpen || !!event.competitionStage }
  );

  // Two-stage competitions gate the offer on the invitation (ParticipationCta), not registration.
  if (!isLoggedInWithOrg || !isOpen || event.competitionStage || !status?.hasRegistered) return null;

  const start = async () => {
    try {
      const bid = await createBid({ eventId: event.id }).unwrap();
      navigate(urls.portal.bid.edit.go(event.id, bid.id).intro);
    } catch (e) {
      displayer.trigger(e);
    }
  };

  return (
    <Card variant="filled" bg="smPrimarySofter" rounded="xl">
      <CardBody>
        <Flex direction={{ base: "column", md: "row" }} align={{ md: "center" }} justify="space-between" gap="4">
          <Stack spacing="1">
            <Text fontWeight="semibold">{t("Want to compete for this contract?")}</Text>
            <Text fontSize="sm" color="smMuted">
              {t("Build your offer step by step. We save your progress automatically.")}
            </Text>
          </Stack>
          <Button
            colorScheme="teal"
            leftIcon={<Icon as={FiEdit3} />}
            onClick={start}
            isLoading={isLoading}
            flexShrink={0}
          >
            {t("Start offer")}
          </Button>
        </Flex>
      </CardBody>
    </Card>
  );
};
